<!-- app-subdomain-creators\src\accountPages\NotificationsPageCreator.vue -->
<template>
  <div class="px-4 sm:px-6 lg:px-56">
    <NotificationSlugModalCreator
      :is-open="isNotificationModalOpen"
      :notification="selectedNotification"
      @close="closeNotificationModal"
    />

    <div class="flex items-center pb-6 pt-2">
      <h1 class="text-xl font-semibold text-zinc-700">
        Notification Hub
      </h1>
    </div>

    <div class="mb-6 flex gap-3 ">
      <button
        type="button"
        class="border-b-2 px-1 pb-2 text-base font-medium transition-colors"
        :class="activeTab === 'notifications' ? 'border-[#2D8CFF] text-[#2D8CFF]' : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-b-zinc-300'"
        @click="activeTab = 'notifications'"
      >
        Notifications
      </button>
      <button
        type="button"
        class="border-b-2 px-1 pb-2 text-base font-medium transition-colors"
        :class="activeTab === 'preferences' ? 'border-[#2D8CFF] text-[#2D8CFF]' : 'border-transparent text-zinc-500 hover:text-zinc-700 hover:border-b-zinc-300'"
        @click="openPreferencesTab"
      >
        Preferences
      </button>
    </div>

    <div v-if="activeTab === 'notifications'">
      <div
        v-if="notifications.length === 0"
        class="flex flex-col items-center justify-center py-20"
      >
        <p class="text-lg font-medium text-zinc-700">
          No Notifications Yet
        </p>
        <p class="text-sm text-zinc-500">
          All caught up! Stay tuned for updates.
        </p>
      </div>

      <div v-else>
        <div class="mb-4 flex items-center justify-between p-2">
          <div class="flex items-center gap-3 px-[19px]">
            <span class="h-3 w-3 shrink-0" />
            <input
              v-model="selectAll"
              type="checkbox"
              class="h-5 w-5 cursor-pointer rounded-md border border-zinc-300 bg-white text-[#2D8CFF] shadow-sm transition-colors duration-150 focus:ring-2 focus:ring-[#2D8CFF]/40 focus:ring-offset-1"
              @change="toggleSelectAll"
            >
            <span class="text-sm text-zinc-500">Select All</span>
          </div>

          <div
            v-if="selectedSlugs.length > 0 || selectAll"
            class="mr-4 flex items-center gap-4"
          >
            <button
              class="text-sm text-zinc-500 hover:text-zinc-400"
              @click="markSelectedAsRead"
            >
              Mark Selected as Read
            </button>
            <button
              class="text-sm text-zinc-500 hover:text-zinc-400"
              @click="deleteSelected"
            >
              Delete Selected
            </button>
          </div>
        </div>

        <ul
          ref="notificationsListRef"
          class="max-h-[calc(75vh-4rem)] overflow-x-auto overflow-y-auto app-scroll rounded-xl px-3"
        >
          <li
            v-for="notification in sortedNotifications"
            :key="notification.notificationSlug"
            class="relative my-3 cursor-pointer rounded-md border border-zinc-100 bg-white px-2 py-3 shadow-sm hover:bg-zinc-100"
            @click="openNotificationModal(notification)"
          >
            <div class="flex items-center justify-between gap-4">
              <div class="flex min-w-0 items-center gap-4">
                <span
                  class="h-3 w-3 shrink-0 rounded-full"
                  :class="notification.isRead ? 'bg-transparent' : 'bg-[#2D8CFF]'"
                />
                <input
                  v-model="selectedSlugs"
                  :value="notification.notificationSlug"
                  type="checkbox"
                  class="h-5 w-5 cursor-pointer rounded-md border border-zinc-300 bg-white text-[#2D8CFF] shadow-sm transition-colors duration-150 focus:ring-2 focus:ring-[#2D8CFF]/40 focus:ring-offset-1"
                  @click.stop
                  @change="handleRowSelectionChange(notification.notificationSlug, $event)"
                >

                <div class="flex min-w-0 items-center gap-2">
                  <div class="relative inline-flex shrink-0">
                    <img
                      :src="getAuthorAvatar(notification)"
                      alt="Author avatar"
                      :class="getNotificationAvatarClass(notification)"
                      @error="onAvatarError"
                    >
                    <img
                      v-if="getReactionIcon(notification)"
                      :src="getReactionIcon(notification)"
                      alt="Reaction"
                      class="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-white ring-1 ring-white"
                    >
                  </div>
                  <span
                    class="max-w-[700px] truncate"
                    :class="[notification.isRead ? 'font-normal' : 'font-medium', getNotificationDisplayClass(notification)]"
                  >
                    {{ getNotificationDisplayText(notification) }}
                  </span>
                </div>
              </div>

              <div class="flex shrink-0 items-center gap-2">
                <span class="text-sm text-zinc-500">{{ formatDate(notification.createdAt) }}</span>
                <CheckCircleIcon
                  :class="notification.isRead ? 'text-zinc-300 hover:text-zinc-500' : 'text-zinc-500 hover:text-zinc-400'"
                  class="h-6 w-6 cursor-pointer"
                  @click.stop="toggleRead(notification)"
                />
                <XCircleIcon
                  class="h-6 w-6 cursor-pointer text-zinc-500 hover:text-zinc-400"
                  @click.stop="deleteOne(notification)"
                />
              </div>
            </div>
          </li>
          <li
            v-if="hasMoreNotifications"
            ref="notificationsSentinelRef"
            class="py-3 text-center text-sm text-zinc-400"
          >
            {{ notificationsLoadingMore ? 'Loading more...' : 'Scroll for more' }}
          </li>
        </ul>
      </div>
    </div>

    <div
      v-else
      class="rounded-xl border border-zinc-200 bg-white shadow-sm"
    >
      <div class="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
        <div>
          <h2 class="text-base font-semibold text-zinc-800">
            Notification Preferences
          </h2>
          <p class="text-sm text-zinc-500">
            Manage your notifications and what members are allowed to control.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
            :disabled="preferencesSaving || preferencesLoading"
            @click="setAllPreferences(false)"
          >
            Turn all off
          </button>
          <button
            type="button"
            class="rounded-md bg-[#2D8CFF] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#2278dd] disabled:opacity-40"
            :disabled="preferencesSaving || preferencesLoading"
            @click="setAllPreferences(true)"
          >
            Turn all on
          </button>
        </div>
      </div>

      <div
        v-if="preferencesLoading"
        class="px-5 py-6 text-sm text-zinc-500"
      >
        Loading preferences...
      </div>
      <div
        v-else-if="!preferences.length"
        class="px-5 py-6 text-sm text-zinc-500"
      >
        No preferences available yet.
      </div>
      <div
        v-else
        class="divide-y divide-zinc-200"
      >
        <div
          v-for="pref in preferences"
          :key="pref.key"
          class="px-5 py-5"
        >
          <div class="flex items-center justify-between gap-4 py-1">
            <div class="min-w-0">
              <h3 class="text-sm font-semibold text-zinc-800">
                {{ pref.label }}
              </h3>
              <p class="mt-1 text-sm text-zinc-500">
                {{ pref.description }}
              </p>
            </div>

            <div class="group relative">
              <button
                type="button"
                role="switch"
                :aria-checked="pref.enabled ? 'true' : 'false'"
                :class="[
                  'group relative inline-flex w-11 shrink-0 rounded-full p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-[#2D8CFF] transition-colors duration-200 ease-in-out',
                  pref.enabled ? 'bg-[#2D8CFF]' : 'bg-gray-200',
                  preferencesSaving ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                ]"
                :disabled="preferencesSaving"
                @click="setPreferenceEnabled(pref, !pref.enabled)"
              >
                <span
                  aria-hidden="true"
                  class="size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out"
                  :class="pref.enabled ? 'translate-x-5' : 'translate-x-0'"
                />
              </button>
              <span class="pointer-events-none absolute -top-8 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-zinc-900 px-2 py-1 text-xs font-medium text-white shadow-sm group-hover:inline-block">
                {{ pref.enabled ? 'On' : 'Off' }}
              </span>
            </div>
          </div>

          <div class="flex items-center justify-between gap-4 py-3">
            <div class="min-w-0">
              <p class="text-sm font-medium text-zinc-700">
                Member Control
              </p>
              <p class="text-xs text-zinc-500">
                Allow members to change this preference.
              </p>
            </div>

            <div class="flex items-center gap-3">
              <div class="group relative">
                <button
                  type="button"
                  role="switch"
                  :aria-checked="pref.memberCanOverride ? 'true' : 'false'"
                  :class="[
                    'group relative inline-flex w-11 shrink-0 rounded-full p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-[#2D8CFF] transition-colors duration-200 ease-in-out',
                    pref.memberCanOverride ? 'bg-[#2D8CFF]' : 'bg-gray-200',
                    preferencesSaving ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                  ]"
                  :disabled="preferencesSaving"
                  @click="setPreferenceMemberControl(pref, !pref.memberCanOverride)"
                >
                  <span
                    aria-hidden="true"
                    class="size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out"
                    :class="pref.memberCanOverride ? 'translate-x-5' : 'translate-x-0'"
                  />
                </button>
                <span class="pointer-events-none absolute -top-8 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-zinc-900 px-2 py-1 text-xs font-medium text-white shadow-sm group-hover:inline-block">
                  {{ pref.memberCanOverride ? 'Members can change' : 'Members locked' }}
                </span>
              </div>

              <div
                v-if="!pref.memberCanOverride"
                class="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1"
              >
                <LockClosedIcon class="h-4 w-4 text-zinc-500" />
                <span class="text-xs font-medium text-zinc-600">Members</span>
                <div class="group relative">
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="pref.memberGlobalEnabled ? 'true' : 'false'"
                    :class="[
                      'group relative inline-flex w-11 shrink-0 rounded-full p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-[#2D8CFF] transition-colors duration-200 ease-in-out',
                      pref.memberGlobalEnabled ? 'bg-[#2D8CFF]' : 'bg-gray-200',
                      preferencesSaving ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
                    ]"
                    :disabled="preferencesSaving"
                    @click="setLockedMemberState(pref, !pref.memberGlobalEnabled)"
                  >
                    <span
                      aria-hidden="true"
                      class="size-5 rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out"
                      :class="pref.memberGlobalEnabled ? 'translate-x-5' : 'translate-x-0'"
                    />
                  </button>
                  <span class="pointer-events-none absolute -top-8 left-1/2 z-10 hidden -translate-x-1/2 whitespace-nowrap rounded-md bg-zinc-900 px-2 py-1 text-xs font-medium text-white shadow-sm group-hover:inline-block">
                    {{ pref.memberGlobalEnabled ? 'Members receive this' : 'Members do not receive this' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from 'vue';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline';
import { LockClosedIcon } from '@heroicons/vue/20/solid';
import { useRoute, useRouter } from 'vue-router';
import { useWebsocketStore } from '@/stores/websocketStore';
import NotificationSlugModalCreator from '@/Components/NotificationSlugModalCreator.vue';
import defaultAvatar from '@/assets/media/images/default-avatar.png';
const btcIcon = new URL('@/assets/media/images/btc_icon_Platforma_Icons.svg', import.meta.url).href;
const likeIcon = new URL('@/assets/media/images/newsfeed-like-icon.png', import.meta.url).href;
const loveIcon = new URL('@/assets/media/images/newsfeed-love-icon.png', import.meta.url).href;
const careIcon = new URL('@/assets/media/images/newsfeed-care-icon.png', import.meta.url).href;
const hahaIcon = new URL('@/assets/media/images/newsfeed-haha-icon.png', import.meta.url).href;
const wowIcon = new URL('@/assets/media/images/newsfeed-wow-icon.png', import.meta.url).href;
const sadIcon = new URL('@/assets/media/images/newsfeed-sad-icon.png', import.meta.url).href;
const angryIcon = new URL('@/assets/media/images/newsfeed-angry-icon.png', import.meta.url).href;
const commentIcon = new URL('@/assets/media/images/comment-platforma-newsfeed-icon.png', import.meta.url).href;
const postIcon = new URL('@/assets/media/images/post-platforma-newsfeed-icon.png', import.meta.url).href;

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL || '';
const app = getCurrentInstance();
const notify = app?.appContext?.config?.globalProperties?.$notify;

const DEFAULT_CREATOR_PREFERENCES = [
  {
    key: 'newsfeed_posts',
    label: 'Newsfeed Posts',
    description: 'Receive notifications when a new newsfeed post is created.',
    enabled: true,
    memberGlobalEnabled: true,
    memberCanOverride: true,
  },
  {
    key: 'newsfeed_comments',
    label: 'All Newsfeed Comments',
    description: 'Receive notifications when any new comment is added to newsfeed posts.',
    enabled: true,
    memberGlobalEnabled: true,
    memberCanOverride: true,
  },
  {
    key: 'newsfeed_comments_on_my_posts',
    label: 'Comments On Your Posts',
    description: 'Receive notifications when someone comments on your own newsfeed posts.',
    enabled: true,
    memberGlobalEnabled: true,
    memberCanOverride: true,
  },
  {
    key: 'newsfeed_replies',
    label: 'All Newsfeed Replies',
    description: 'Receive notifications when any new reply is added to a newsfeed comment thread.',
    enabled: true,
    memberGlobalEnabled: true,
    memberCanOverride: true,
  },
  {
    key: 'newsfeed_replies_on_my_comments',
    label: 'Replies On Your Comments',
    description: 'Receive notifications when someone replies to your comments.',
    enabled: true,
    memberGlobalEnabled: true,
    memberCanOverride: true,
  },
  {
    key: 'newsfeed_post_reactions',
    label: 'Post Reactions',
    description: 'Receive notifications when someone reacts to a newsfeed post.',
    enabled: true,
    memberGlobalEnabled: true,
    memberCanOverride: true,
  },
  {
    key: 'newsfeed_comment_reply_reactions',
    label: 'Comment & Reply Reactions',
    description: 'Receive notifications when someone reacts to a comment or reply.',
    enabled: true,
    memberGlobalEnabled: true,
    memberCanOverride: true,
  },
];

const notifications = ref([]);
const selectedSlugs = ref([]);
const selectAll = ref(false);
const deselectedSlugs = ref([]);
const notificationsListRef = ref(null);
const notificationsSentinelRef = ref(null);
const notificationsOffset = ref(0);
const notificationsLimit = 40;
const hasMoreNotifications = ref(true);
const notificationsLoadingMore = ref(false);
let notificationsObserver = null;
const isNotificationModalOpen = ref(false);
const selectedNotification = ref(null);
const activeTab = ref('notifications');
const preferences = ref([]);
const preferencesLoading = ref(false);
const preferencesSaving = ref(false);

let intervalId = null;
let wsUnsubscribe = null;
const websocketStore = useWebsocketStore();
const route = useRoute();
const router = useRouter();
const creatorNotificationsBasePath = '/creator/notifications';

function emitUnreadSync() {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('notifications:unread-sync'));
}

const sortedNotifications = computed(() =>
  [...notifications.value].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
);

function parseNotificationPayload(notification) {
  const raw =
    notification?.payload ??
    notification?.payloadJson ??
    notification?.payload_json ??
    notification?.metadata ??
    notification?.meta;
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  let current = String(raw);
  for (let index = 0; index < 3; index += 1) {
    if (current && typeof current === 'object') return current;
    if (typeof current !== 'string') break;
    const trimmed = current.trim();
    if (!trimmed) break;
    try {
      current = JSON.parse(trimmed);
    } catch {
      break;
    }
  }
  return {};
}

function normalizeAvatarSrc(value, featureInstanceId) {
  const raw = String(value || '').trim();
  if (!raw) return defaultAvatar;
  const match = raw.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (!match?.[1]) return raw;

  const params = new URLSearchParams();
  const iid = Number(featureInstanceId || 0);
  if (Number.isFinite(iid) && iid > 0) {
    params.set('featureInstanceId', String(iid));
  }
  const qs = params.toString();
  return `${apiProxyUrl}/api/creator-newsfeed/image/${match[1]}${qs ? `?${qs}` : ''}`;
}

function onAvatarError(event) {
  const img = event?.target;
  if (!img || img.tagName !== 'IMG') return;
  img.src = defaultAvatar;
}

function resolveVideoPublishEventType(notification, payload = {}) {
  const eventType = String(
    notification?.eventType ||
      notification?.event_type ||
      payload?.eventType ||
      payload?.event_type ||
      payload?.type ||
      payload?.notificationType ||
      payload?.notification_type ||
      payload?.category ||
      payload?.source ||
      payload?.source_type ||
      ''
  ).toLowerCase().trim();
  if (eventType === 'video_channel_video_published' || eventType === 'video-channel-video-published' || eventType.includes('video_channel') || eventType.includes('video-channel')) return 'video-channel';
  if (eventType === 'flix_theatre_video_published' || eventType === 'flix-theatre-video-published' || eventType.includes('flix_theatre') || eventType.includes('flix-theatre')) return 'flix-theatre';
  const hasFlixHints = Boolean(
      payload?.theatreLogoMediaFileId ||
      payload?.theatre_logo_media_file_id ||
      payload?.theatreAvatarMediaFileId ||
      payload?.theatre_avatar_media_file_id ||
      payload?.theatreLogoUrl ||
      payload?.theatre_logo_url ||
      payload?.theatreAvatarUrl ||
      payload?.theatre_avatar_url ||
      payload?.theatreTitle ||
      payload?.theatre_title ||
      payload?.flixTitle ||
      payload?.flix_title
  );
  if (hasFlixHints) return 'flix-theatre';
  const text = String(
    notification?.title ||
      notification?.body ||
      payload?.title ||
      payload?.body ||
      ''
  ).toLowerCase().trim();
  if (text.includes('flix theatre') || (text.includes('streaming release') && text.includes('flix'))) {
    return 'flix-theatre';
  }
  return '';
}

function isVideoPublishNotification(notification, payload = {}) {
  return !!resolveVideoPublishEventType(notification, payload);
}
function isFlixVideoPublishNotification(notification, payload = {}) {
  return resolveVideoPublishEventType(notification, payload) === 'flix-theatre';
}
function resolveStorefrontNotificationEventType(notification, payload = {}) {
  const eventType = String(
    notification?.eventType ||
      notification?.event_type ||
      payload?.eventType ||
      payload?.event_type ||
      payload?.type ||
      payload?.notificationType ||
      payload?.notification_type ||
      payload?.source ||
      ''
  ).toLowerCase().trim();
  if (eventType.startsWith('storefront_')) return eventType;
  return '';
}
function isStorefrontNotification(notification, payload = {}) {
  return !!resolveStorefrontNotificationEventType(notification, payload);
}
function resolveNotificationEventType(notification, payload = {}) {
  return String(
    notification?.eventType ||
      notification?.event_type ||
      payload?.eventType ||
      payload?.event_type ||
      payload?.type ||
      payload?.notificationType ||
      payload?.notification_type ||
      payload?.category ||
      payload?.source ||
      payload?.source_type ||
      ''
  ).toLowerCase().trim();
}
function isCreatorMemberSubscriptionPaymentFailedNotification(notification, payload = {}) {
  const eventType = resolveNotificationEventType(notification, payload);
  return (
    eventType === 'member_subscription_payment_failed' ||
    eventType === 'member.subscription_payment_failed' ||
    eventType === 'subscription_payment_failed'
  );
}
function isCryptoReceiveNotification(notification, payload = {}) {
  const eventType = resolveNotificationEventType(notification, payload);
  return eventType === 'crypto_btc_received';
}
function resolveStorefrontMemberAvatar(notification, payload = {}) {
  const raw = String(
    payload?.memberAvatarUrl ||
      notification?.memberAvatarUrl ||
      payload?.member_avatar_url ||
      notification?.member_avatar_url ||
      payload?.avatarUrl ||
      notification?.avatarUrl ||
      payload?.avatar_url ||
      notification?.avatar_url ||
      ''
  ).trim();
  if (!raw) return '';
  return normalizeAvatarSrc(
    raw,
    notification?.featureInstanceId || notification?.feature_instance_id
  );
}
function buildStorefrontOrderDisplayText(notification, payload = {}) {
  const storefrontName = String(
    payload?.storefrontName ||
      payload?.storefront_name ||
      payload?.storeName ||
      payload?.store_name ||
      payload?.featureInstanceName ||
      payload?.instanceName ||
      payload?.storefrontTitle ||
      'Storefront'
  ).trim() || 'Storefront';
  const memberName = String(
    payload?.memberName || payload?.member_name || notification?.title || 'Member'
  ).trim() || 'Member';
  const orderNumber = String(
    payload?.orderNumber || payload?.order_number || notification?.orderNumber || ''
  ).trim();
  if (orderNumber) {
    return `${storefrontName} Order #${orderNumber} placed by ${memberName}`;
  }
  return `${storefrontName} Order placed by ${memberName}`;
}

function resolveCreatorVideoPublishAvatar(notification, payload = {}) {
  const eventType = resolveVideoPublishEventType(notification, payload);
  if (!eventType) return '';
  const featureInstanceId = Number(
    notification?.featureInstanceId ||
      notification?.feature_instance_id ||
      payload?.featureInstanceId ||
      payload?.feature_instance_id ||
      payload?.instanceId ||
      payload?.instance_id ||
      payload?.feature?.instanceId ||
      payload?.feature?.instance_id ||
      0
  );
  if (eventType === 'flix-theatre') {
    const logoFileId = String(
      payload?.creatorLogoMediaFileId ||
        notification?.creatorLogoMediaFileId ||
        payload?.creator_logo_media_file_id ||
        notification?.creator_logo_media_file_id ||
        payload?.theatreLogoMediaFileId ||
        notification?.theatreLogoMediaFileId ||
        payload?.theatre_logo_media_file_id ||
        notification?.theatre_logo_media_file_id ||
        payload?.theatreAvatarMediaFileId ||
        notification?.theatreAvatarMediaFileId ||
        payload?.theatre_avatar_media_file_id ||
        notification?.theatre_avatar_media_file_id ||
        payload?.channelAvatarMediaFileId ||
        notification?.channelAvatarMediaFileId ||
        payload?.channel_avatar_media_file_id ||
        notification?.channel_avatar_media_file_id ||
        ''
    ).trim();
    if (logoFileId && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
      const qs = new URLSearchParams({
        featureInstanceId: String(Math.trunc(featureInstanceId)),
      }).toString();
      return `${apiProxyUrl}/api/creator-flix-theatre/image/${encodeURIComponent(logoFileId)}?${qs}`;
    }
    const directLogo = String(
      payload?.creatorLogoUrl ||
        notification?.creatorLogoUrl ||
        payload?.creator_logo_url ||
        notification?.creator_logo_url ||
        payload?.theatreLogoUrl ||
        notification?.theatreLogoUrl ||
        payload?.theatre_logo_url ||
        notification?.theatre_logo_url ||
        payload?.creatorAvatarUrl ||
        notification?.creatorAvatarUrl ||
        payload?.creator_avatar_url ||
        notification?.creator_avatar_url ||
        payload?.avatarUrl ||
        notification?.avatarUrl ||
        payload?.avatar_url ||
        notification?.avatar_url ||
        payload?.authorAvatarUrl ||
        notification?.authorAvatarUrl ||
        payload?.author_avatar_url ||
        notification?.author_avatar_url ||
        payload?.authorAvatar ||
        notification?.authorAvatar ||
        payload?.channelAvatarUrl ||
        notification?.channelAvatarUrl ||
        payload?.channel_avatar_url ||
        notification?.channel_avatar_url ||
        payload?.theatreAvatarUrl ||
        notification?.theatreAvatarUrl ||
        payload?.theatre_avatar_url ||
        notification?.theatre_avatar_url ||
        payload?.logoUrl ||
        notification?.logoUrl ||
        payload?.logo_url ||
        notification?.logo_url ||
        ''
    ).trim();
    if (directLogo) return directLogo;
    return `${apiProxyUrl}/api/main-app-logo`;
  }
  const fileId = String(
    payload?.channelAvatarMediaFileId ||
      payload?.channel_avatar_media_file_id ||
      payload?.theatreAvatarMediaFileId ||
      payload?.theatre_avatar_media_file_id ||
      ''
  ).trim();
  if (fileId && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(Math.trunc(featureInstanceId)),
    }).toString();
    const imagePrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/image/'
        : '/api/creator-video-channel/image/';
    return `${apiProxyUrl}${imagePrefix}${encodeURIComponent(fileId)}?${qs}`;
  }

  const direct = String(
    payload?.creatorAvatarUrl ||
      payload?.creator_avatar_url ||
      payload?.theatreAvatarUrl ||
      payload?.theatre_avatar_url ||
      payload?.channelAvatarUrl ||
      payload?.channel_avatar_url ||
      payload?.authorAvatar ||
      payload?.authorAvatarUrl ||
      payload?.author_avatar_url ||
      payload?.avatarUrl ||
      payload?.avatar_url ||
      ''
  ).trim();
  const match = direct.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (match?.[1] && Number.isFinite(featureInstanceId) && featureInstanceId > 0) {
    const qs = new URLSearchParams({
      featureInstanceId: String(Math.trunc(featureInstanceId)),
    }).toString();
    const imagePrefix =
      eventType === 'flix-theatre'
        ? '/api/creator-flix-theatre/image/'
        : '/api/creator-video-channel/image/';
    return `${apiProxyUrl}${imagePrefix}${encodeURIComponent(match[1])}?${qs}`;
  }
  return direct || '';
}

function isBtcAvatarSrc(value) {
  const src = String(value || '').trim();
  return src.includes('btc_icon_Platforma_Icons.svg');
}

function getAuthorAvatar(notification) {
  const payload = parseNotificationPayload(notification);
  if (isCryptoReceiveNotification(notification, payload)) return btcIcon;
  if (isStorefrontNotification(notification, payload)) {
    const storefrontMemberAvatar = resolveStorefrontMemberAvatar(notification, payload);
    if (storefrontMemberAvatar) return storefrontMemberAvatar;
  }
  if (isVideoPublishNotification(notification, payload)) {
    const direct = resolveCreatorVideoPublishAvatar(notification, payload);
    if (direct) return direct;
  }
  const raw = String(
    payload?.memberAvatarUrl ||
      payload?.member_avatar_url ||
      payload?.authorAvatar ||
      payload?.authorAvatarUrl ||
      payload?.author_avatar_url ||
      payload?.avatarUrl ||
      payload?.avatar_url ||
      notification?.authorAvatar ||
      notification?.authorAvatarUrl ||
      notification?.author_avatar_url ||
      notification?.avatarUrl ||
      notification?.avatar_url ||
      ''
  ).trim();
  return normalizeAvatarSrc(
    raw,
    notification?.featureInstanceId || notification?.feature_instance_id
  );
}
function getNotificationAvatarClass(notification) {
  const payload = parseNotificationPayload(notification);
  const avatarSrc = getAuthorAvatar(notification);
  if (isCryptoReceiveNotification(notification, payload) || isBtcAvatarSrc(avatarSrc)) {
    return 'h-8 w-8 shrink-0 rounded-full object-cover scale-[1.12]';
  }
  if (isStorefrontNotification(notification, payload)) {
    return 'h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-zinc-200';
  }
  if (isFlixVideoPublishNotification(notification, payload)) {
    return 'h-8 w-auto max-w-[96px] shrink-0 object-contain';
  }
  return 'h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-zinc-200';
}
function getNotificationDisplayText(notification) {
  const payload = parseNotificationPayload(notification);
  const storefrontEventType = resolveStorefrontNotificationEventType(notification, payload);
  if (storefrontEventType === 'storefront_low_inventory') {
    const title = String(notification?.title || '').trim();
    const body = String(notification?.body || '').trim();
    return [title, body].filter(Boolean).join(' ');
  }
  if (storefrontEventType) {
    return buildStorefrontOrderDisplayText(notification, payload);
  }
  if (isFlixVideoPublishNotification(notification, payload)) {
    const featureName = String(
      payload?.theatreTitle || notification?.title || 'Flix Theatre'
    ).trim() || 'Flix Theatre';
    return `New Streaming Release in ${featureName}`;
  }
  const title = String(notification?.title || '').trim();
  const body = String(notification?.body || '').trim();
  return [title, body].filter(Boolean).join(' ');
}
function getNotificationDisplayClass(notification) {
  const payload = parseNotificationPayload(notification);
  if (isCreatorMemberSubscriptionPaymentFailedNotification(notification, payload)) {
    return notification?.isRead ? 'text-red-500' : 'text-red-600';
  }
  return notification?.isRead ? 'text-zinc-500' : 'text-zinc-700';
}

function getReactionIcon(notification) {
  const payload = parseNotificationPayload(notification);
  if (isCryptoReceiveNotification(notification, payload)) return '';
  const eventType = String(
    notification?.eventType || payload?.eventType || payload?.source || ''
  ).toLowerCase().trim();
  const type = String(payload?.reactionType || '').toLowerCase().trim();
  if (type === 'like') return likeIcon;
  if (type === 'love') return loveIcon;
  if (type === 'care') return careIcon;
  if (type === 'haha') return hahaIcon;
  if (type === 'wow') return wowIcon;
  if (type === 'sad') return sadIcon;
  if (type === 'angry') return angryIcon;
  if (
    eventType === 'newsfeed_post_created'
  ) {
    return postIcon;
  }
  if (
    eventType === 'newsfeed_comment_created' ||
    eventType === 'newsfeed_comment_on_your_post' ||
    eventType === 'newsfeed_reply_created' ||
    eventType === 'newsfeed_reply_on_your_comment'
  ) {
    return commentIcon;
  }
  return '';
}

function showPreferenceSavedToast() {
  if (typeof notify !== 'function') return;
  notify({
    title: 'Preferences updated',
    body: 'Your notification preferences were saved.',
    variant: 'success',
    duration: 3000,
  });
}

function showPreferenceErrorToast() {
  if (typeof notify !== 'function') return;
  notify({
    title: 'Update failed',
    body: 'Could not save notification preferences.',
    variant: 'error',
    duration: 3500,
  });
}

function applyCreatorPreferenceUpdatesLocally(updates) {
  const byKey = new Map(
    (Array.isArray(updates) ? updates : [])
      .map((u) => [String(u?.key || ''), u])
      .filter(([k]) => k)
  );

  preferences.value = (preferences.value || []).map((pref) => {
    const next = byKey.get(String(pref?.key || ''));
    if (!next) return pref;
    return {
      ...pref,
      enabled: next.enabled == null ? pref.enabled : Boolean(next.enabled),
      memberGlobalEnabled:
        next.memberGlobalEnabled == null
          ? pref.memberGlobalEnabled
          : Boolean(next.memberGlobalEnabled),
      memberCanOverride:
        next.memberCanOverride == null
          ? pref.memberCanOverride
          : Boolean(next.memberCanOverride),
    };
  });
}

function withCreatorPreferenceDefaults(list) {
  const rows = Array.isArray(list) ? list : [];
  const byKey = new Map(rows.map((r) => [String(r?.key || ''), r]));
  return DEFAULT_CREATOR_PREFERENCES.map((d) => {
    const row = byKey.get(d.key);
    return {
      ...d,
      ...(row || {}),
      enabled: row?.enabled ?? d.enabled,
      memberGlobalEnabled:
        row?.memberGlobalEnabled ?? row?.enabled ?? d.memberGlobalEnabled,
      memberCanOverride: row?.memberCanOverride ?? d.memberCanOverride,
    };
  });
}

async function fetchPreferences() {
  preferencesLoading.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/notifications/preferences`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    preferences.value = withCreatorPreferenceDefaults(data?.preferences);
  } catch (error) {
    console.error('[NotificationsPageCreator] fetchPreferences error:', error);
    preferences.value = withCreatorPreferenceDefaults([]);
  } finally {
    preferencesLoading.value = false;
  }
}

async function savePreferenceUpdates(updates) {
  if (!Array.isArray(updates) || !updates.length) return;
  const previous = (preferences.value || []).map((p) => ({ ...p }));
  applyCreatorPreferenceUpdatesLocally(updates);
  preferencesSaving.value = true;
  try {
    const response = await fetch(`${apiProxyUrl}/api/notifications/preferences`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(`preferences update failed (${response.status}) ${text}`);
    }
    const data = await response.json().catch(() => ({}));
    if (Array.isArray(data?.preferences) && data.preferences.length) {
      preferences.value = withCreatorPreferenceDefaults(data.preferences);
    } else {
      await fetchPreferences();
    }
    showPreferenceSavedToast();
  } catch (error) {
    console.error('[NotificationsPageCreator] savePreferenceUpdates error:', error);
    preferences.value = previous;
    showPreferenceErrorToast();
  } finally {
    preferencesSaving.value = false;
  }
}

async function setPreferenceEnabled(pref, enabled) {
  if (!pref) return;
  await savePreferenceUpdates([{ key: pref.key, enabled: Boolean(enabled) }]);
}

async function setPreferenceMemberControl(pref, memberCanOverride) {
  if (!pref) return;
  await savePreferenceUpdates([
    {
      key: pref.key,
      enabled: pref.enabled,
      memberCanOverride: Boolean(memberCanOverride),
    },
  ]);
}

async function setLockedMemberState(pref, enabled) {
  if (!pref) return;
  await savePreferenceUpdates([
    {
      key: pref.key,
      memberCanOverride: false,
      memberGlobalEnabled: Boolean(enabled),
    },
  ]);
}

async function setAllPreferences(enabled) {
  const allowedKeys = new Set(DEFAULT_CREATOR_PREFERENCES.map((p) => p.key));
  const updates = (preferences.value || [])
    .filter((p) => allowedKeys.has(String(p?.key || '')))
    .map((p) => ({
      key: p.key,
      enabled: Boolean(enabled),
    }));
  if (!updates.length) return;
  await savePreferenceUpdates(updates);
}

async function openPreferencesTab() {
  activeTab.value = 'preferences';
  if (!preferences.value.length) {
    await fetchPreferences();
  }
}

async function openNotificationModal(notification, { syncRoute = true } = {}) {
  selectedNotification.value = notification || null;
  isNotificationModalOpen.value = true;
  markOpened(notification);
  if (!notification?.isRead) {
    markAsRead(notification);
  }

  const slug = String(notification?.notificationSlug || '').trim();
  if (!syncRoute || !slug) return;

  const targetPath = `${creatorNotificationsBasePath}/${encodeURIComponent(slug)}`;
  if (route.path !== targetPath) {
    await router.push(targetPath);
  }
}

async function closeNotificationModal({ syncRoute = true } = {}) {
  isNotificationModalOpen.value = false;
  selectedNotification.value = null;

  const routeSlug = String(route.params?.notificationSlug || '').trim();
  if (!syncRoute || !routeSlug) return;
  await router.replace(creatorNotificationsBasePath);
}

async function openModalFromRouteSlug(notificationSlug) {
  const safeSlug = String(notificationSlug || '').trim();
  if (!safeSlug) {
    await closeNotificationModal({ syncRoute: false });
    return;
  }

  let row = notifications.value.find(
    (n) => String(n?.notificationSlug || '').trim() === safeSlug
  );
  if (!row) {
    await fetchNotifications({ reset: true });
    row = notifications.value.find(
      (n) => String(n?.notificationSlug || '').trim() === safeSlug
    );
  }
  if (!row) {
    await fetchNotificationsUntilSlug(safeSlug);
    row = notifications.value.find(
      (n) => String(n?.notificationSlug || '').trim() === safeSlug
    );
  }

  if (!row) {
    await closeNotificationModal({ syncRoute: false });
    return;
  }

  await openNotificationModal(row, { syncRoute: false });
}

function formatDate(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function dedupeNotifications(list) {
  const bySlug = new Map();
  for (const row of Array.isArray(list) ? list : []) {
    const slug = String(row?.notificationSlug || '').trim();
    if (!slug) continue;
    bySlug.set(slug, row);
  }
  return Array.from(bySlug.values());
}

function setupNotificationsObserver() {
  if (notificationsObserver) {
    notificationsObserver.disconnect();
    notificationsObserver = null;
  }
  if (!notificationsSentinelRef.value || !notificationsListRef.value) return;
  notificationsObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries?.[0];
      if (!entry?.isIntersecting) return;
      if (!hasMoreNotifications.value || notificationsLoadingMore.value) return;
      void fetchNotifications({ append: true });
    },
    {
      root: notificationsListRef.value,
      rootMargin: '0px 0px 200px 0px',
      threshold: 0.01,
    }
  );
  notificationsObserver.observe(notificationsSentinelRef.value);
}

async function fetchNotifications({ append = false, reset = false } = {}) {
  if (notificationsLoadingMore.value) return;
  if (append && !hasMoreNotifications.value) return;

  if (reset) {
    notificationsOffset.value = 0;
    hasMoreNotifications.value = true;
  }
  const offset = append ? notificationsOffset.value : 0;
  notificationsLoadingMore.value = true;

  try {
    const response = await fetch(`${apiProxyUrl}/api/notifications?limit=${notificationsLimit}&offset=${offset}`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await response.json().catch(() => ({}));
    const rows = Array.isArray(data?.notifications) ? data.notifications : [];
    notifications.value = append
      ? dedupeNotifications([...notifications.value, ...rows])
      : rows;
    syncLoadedSelectionForSelectAll();

    notificationsOffset.value = offset + rows.length;
    const total = Number(data?.total || 0);
    hasMoreNotifications.value = rows.length === notificationsLimit && (total ? notificationsOffset.value < total : true);
  } catch (error) {
    console.error('[NotificationsPageCreator] fetchNotifications error:', error);
  } finally {
    notificationsLoadingMore.value = false;
    setTimeout(setupNotificationsObserver, 0);
  }
}

async function fetchNotificationsUntilSlug(slug) {
  let guard = 0;
  while (
    hasMoreNotifications.value &&
    !notifications.value.some((n) => String(n?.notificationSlug || '').trim() === slug) &&
    guard < 20
  ) {
    guard += 1;
    await fetchNotifications({ append: true });
  }
}

function toggleSelectAll() {
  if (selectAll.value) {
    deselectedSlugs.value = [];
    syncLoadedSelectionForSelectAll();
  } else {
    selectedSlugs.value = [];
    deselectedSlugs.value = [];
  }
}

function syncLoadedSelectionForSelectAll() {
  if (!selectAll.value) return;
  const excluded = new Set(deselectedSlugs.value);
  selectedSlugs.value = notifications.value
    .map((n) => String(n?.notificationSlug || '').trim())
    .filter((slug) => slug && !excluded.has(slug));
}

function handleRowSelectionChange(slug, event) {
  const safeSlug = String(slug || '').trim();
  if (!safeSlug || !selectAll.value) return;
  const checked = Boolean(event?.target?.checked);
  if (checked) {
    deselectedSlugs.value = deselectedSlugs.value.filter((s) => s !== safeSlug);
    return;
  }
  if (!deselectedSlugs.value.includes(safeSlug)) {
    deselectedSlugs.value = [...deselectedSlugs.value, safeSlug];
  }
}

async function markAsRead(notification) {
  if (!notification?.notificationSlug || notification.isRead) return;
  try {
    await fetch(
      `${apiProxyUrl}/api/notifications/${encodeURIComponent(notification.notificationSlug)}/read`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    notification.isRead = 1;
    emitUnreadSync();
  } catch (error) {
    console.error('[NotificationsPageCreator] markAsRead error:', error);
  }
}

async function markAsUnread(notification) {
  if (!notification?.notificationSlug || !notification.isRead) return;
  try {
    await fetch(
      `${apiProxyUrl}/api/notifications/${encodeURIComponent(notification.notificationSlug)}/unread`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    notification.isRead = 0;
    emitUnreadSync();
  } catch (error) {
    console.error('[NotificationsPageCreator] markAsUnread error:', error);
  }
}

async function toggleRead(notification) {
  if (notification?.isRead) {
    await markAsUnread(notification);
  } else {
    await markAsRead(notification);
  }
}

async function markOpened(notification) {
  if (!notification?.notificationSlug) return;
  try {
    await fetch(
      `${apiProxyUrl}/api/notifications/${encodeURIComponent(notification.notificationSlug)}/opened`,
      {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      }
    );
  } catch (error) {
    console.error('[NotificationsPageCreator] markOpened error:', error);
  }
}

async function markSelectedAsRead() {
  if (selectAll.value) {
    try {
      const excludeSlugs = [...deselectedSlugs.value];
      await fetch(`${apiProxyUrl}/api/notifications/read-all`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excludeSlugs }),
      });
      const excluded = new Set(excludeSlugs);
      notifications.value = notifications.value.map((n) =>
        excluded.has(String(n?.notificationSlug || '')) ? n : { ...n, isRead: 1 }
      );
      emitUnreadSync();
    } catch (error) {
      console.error('[NotificationsPageCreator] markSelectedAsRead(all) error:', error);
    }
    return;
  }

  for (const slug of selectedSlugs.value) {
    const row = notifications.value.find((n) => n.notificationSlug === slug);
    if (row) {
      await markAsRead(row);
    }
  }
}

async function deleteOne(notification) {
  if (!notification?.notificationSlug) return;
  try {
    await fetch(`${apiProxyUrl}/api/notifications/${encodeURIComponent(notification.notificationSlug)}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    notifications.value = notifications.value.filter(
      (n) => n.notificationSlug !== notification.notificationSlug
    );
    selectedSlugs.value = selectedSlugs.value.filter((s) => s !== notification.notificationSlug);
    emitUnreadSync();
  } catch (error) {
    console.error('[NotificationsPageCreator] deleteOne error:', error);
  }
}

async function deleteSelected() {
  if (selectAll.value) {
    try {
      const excludeSlugs = [...deselectedSlugs.value];
      await fetch(`${apiProxyUrl}/api/notifications/all`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ excludeSlugs }),
      });
      const excluded = new Set(excludeSlugs);
      notifications.value = notifications.value.filter((n) =>
        excluded.has(String(n?.notificationSlug || ''))
      );
      hasMoreNotifications.value = false;
      notificationsOffset.value = notifications.value.length;
      selectedSlugs.value = [];
      deselectedSlugs.value = [];
      selectAll.value = false;
      emitUnreadSync();
    } catch (error) {
      console.error('[NotificationsPageCreator] deleteSelected(all) error:', error);
    }
    return;
  }

  const slugs = [...selectedSlugs.value];
  for (const slug of slugs) {
    const row = notifications.value.find((n) => n.notificationSlug === slug);
    if (row) {
      await deleteOne(row);
    }
  }
  selectAll.value = false;
}

onMounted(() => {
  fetchNotifications({ reset: true });
  intervalId = setInterval(() => fetchNotifications({ reset: true }), 40000);
  wsUnsubscribe = websocketStore.subscribe('notifications:refresh', () => {
    fetchNotifications({ reset: true });
  });
});

watch(activeTab, (tab) => {
  if (tab === 'preferences' && !preferences.value.length && !preferencesLoading.value) {
    fetchPreferences();
  }
});

watch(
  () => route.params?.notificationSlug,
  (nextSlug) => {
    openModalFromRouteSlug(nextSlug);
  },
  { immediate: true }
);

onUnmounted(() => {
  if (intervalId) clearInterval(intervalId);
  if (notificationsObserver) {
    notificationsObserver.disconnect();
    notificationsObserver = null;
  }
  if (wsUnsubscribe) {
    wsUnsubscribe();
    wsUnsubscribe = null;
  }
});

watch(
  () => notificationsSentinelRef.value,
  () => {
    setupNotificationsObserver();
  }
);
</script>


<style scoped>

/* ─────────────────────────────────────────────
   Creator layout scrollbar (right column only)
   ─────────────────────────────────────────── */

/* Firefox base */
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  /* try to keep layout width stable */
  scrollbar-gutter: stable;
}

/* WebKit base (Chrome / Edge / Safari) */
.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

/* Hide old-school buttons/arrows (where they exist) */
.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

/* Track fully invisible so it looks “on top” of content */
.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

/* Thumb invisible by default */
.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

/* On hover over the scroll area: fade thumb in */
.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent; /* Firefox */
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

/* Slightly stronger when directly hovering the thumb */
.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}

</style>
